/**
 * DBE Keeper 패턴 — self-ECDH 디바이스 키 암호화 헬퍼 (Issue #1291).
 *
 * 설계: backend/docs/design/dbe_keeper_pattern_migration.md §4.3
 *
 * Why self-ECDH: WebCrypto 의 extractable=false CryptoKey 는 priv bytes 를 export 할 수
 * 없어 HKDF input 으로 직접 쓸 수 없다. ECDH deriveBits 만이 extractable=false 키를
 * input 으로 받으므로, own priv + own pub 으로 self-derive 한 32B 를 HKDF input 으로 쓴다
 * (priv² × G, deterministic). WebCrypto API 제약 우회이지 보안 회피가 아니며, Keeper Web
 * Vault 도 동등 패턴 (설계 §3.4.3 실측).
 */

const HKDF_SALT = new TextEncoder().encode('safebox-dbe-v1')
const HKDF_INFO = new TextEncoder().encode('vault-key-wrap')

/**
 * 디바이스 ECDH P-256 keypair 생성 (extractable=false).
 * priv 는 IndexedDB 에 직접 저장 — bytes 추출 불가 (XSS 시 raw priv 유출 방어).
 */
export async function generateClientEcdhKeypair() {
  return crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits'])
}

/**
 * self-ECDH derive — own priv + own pub → 32B raw bits → HKDF → wrap_key (AES-GCM-256).
 * priv 없이는 결과 도출 불가 (256-bit 보안). wrap_key 도 extractable=false.
 */
export async function selfDeriveWrapKey(privKey, pubKey) {
  const sharedBits = await crypto.subtle.deriveBits({ name: 'ECDH', public: pubKey }, privKey, 256)

  const material = await crypto.subtle.importKey('raw', sharedBits, 'HKDF', false, ['deriveKey'])

  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: HKDF_SALT, info: HKDF_INFO },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * vault key (RSA private key — JWK 객체 또는 PEM 문자열) 를 wrap_key 로 AES-GCM wrap.
 * iv 는 호출마다 12바이트 random (nonce 재사용 방지).
 *
 * Output: standard base64 (padding 있음 — base64url 아님). 서버 DB TEXT 컬럼 저장.
 */
export async function wrapVaultKey(wrapKey, vaultKey) {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const plaintext = new TextEncoder().encode(JSON.stringify(vaultKey))

  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, plaintext)

  return {
    wrapped_vault_key: bytesToB64(new Uint8Array(ciphertext)),
    iv: bytesToB64(iv),
  }
}

/**
 * wrapped_vault_key + iv 를 wrap_key 로 unwrap → 원본 vault key (JWK 객체 또는 PEM 문자열).
 * 잘못된 wrap_key / 손상된 ciphertext 는 AES-GCM 인증 태그 검증 실패로 reject 된다.
 */
export async function unwrapVaultKey(wrapKey, wrappedB64, ivB64) {
  const ciphertext = b64ToBytes(wrappedB64)
  const iv = b64ToBytes(ivB64)

  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, wrapKey, ciphertext)

  return JSON.parse(new TextDecoder().decode(plaintext))
}

/**
 * client_ecdh_pub JWK 정규화 → SHA-256 → hex 64자.
 * 서버 common/jwk_hash.py 의 normalize_ecdh_pub_jwk 와 동일 (crv/kty/x/y 알파벳순,
 * 공백 없는 compact JSON). user_device.client_ecdh_pub_hash UNIQUE 제약의 클라이언트 검증용.
 */
export async function computeClientEcdhPubHash(pubJwk) {
  const normalized = JSON.stringify({
    crv: pubJwk.crv,
    kty: pubJwk.kty,
    x: pubJwk.x,
    y: pubJwk.y,
  })
  const bytes = new TextEncoder().encode(normalized)
  const hash = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// standard base64 helpers (padding 있음) — module-local
function bytesToB64(bytes) {
  let bin = ''
  bytes.forEach((b) => {
    bin += String.fromCharCode(b)
  })
  return btoa(bin)
}

function b64ToBytes(b64) {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) {
    bytes[i] = bin.charCodeAt(i)
  }
  return bytes
}
